import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { Badge } from '../ui/badge';
import { 
  FeatherPlus,
  FeatherSearch,
  FeatherFilter,
  FeatherEye,
  FeatherEdit2,
  FeatherTrash2,
  FeatherCalendar,
  FeatherUser,
  FeatherTag,
  FeatherFileText,
  FeatherUsers,
  FeatherGrid,
  FeatherClock,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherRefreshCw
} from '../ui/icons';
import api from '../../services/api';
import './NexGateList.css';

const NexGateList = ({ onViewEngagement, onEditEngagement, onCreateNew }) => {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    client: '',
    start_date_from: '',
    start_date_to: '',
    role: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [showFilters, setShowFilters] = useState(false);

  const engagementTypes = [
    { value: 'client_augmentation', label: 'Client Augmentation' },
    { value: 'internal_project', label: 'Internal Project' },
    { value: 'custom_software', label: 'Custom Software' }
  ];

  const statuses = [
    { value: 'new', label: 'New' },
    { value: 'in_review', label: 'In Review' },
    { value: 'fulfilled', label: 'Fulfilled' },
    { value: 'archived', label: 'Archived' }
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  const availableRoles = [
    'Frontend Developer',
    'Backend Developer',
    'Full Stack Developer',
    'DevOps Engineer',
    'QA Engineer',
    'UI/UX Designer',
    'Project Manager',
    'Business Analyst',
    'Data Scientist',
    'Mobile Developer',
    'System Administrator',
    'Technical Writer'
  ];

  useEffect(() => {
    fetchEngagements();
  }, [filters, pagination.page]);

  const fetchEngagements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });

      const response = await api.get(`/api/nexgate?${params}`);
      setEngagements(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      console.error('Error fetching engagements:', error);
      setError('Failed to fetch engagements');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEngagements();
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      status: '',
      client: '',
      start_date_from: '',
      start_date_to: '',
      role: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this engagement?')) {
      return;
    }

    try {
      await api.delete(`/api/nexgate/${id}`);
      fetchEngagements();
    } catch (error) {
      console.error('Error deleting engagement:', error);
      setError('Failed to delete engagement');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'in_review': return 'yellow';
      case 'fulfilled': return 'green';
      case 'archived': return 'gray';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'red';
      default: return 'gray';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'client_augmentation': return <FeatherUsers size={16} />;
      case 'internal_project': return <FeatherGrid size={16} />;
      case 'custom_software': return <FeatherFileText size={16} />;
      default: return <FeatherFileText size={16} />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderEngagementCard = (engagement) => (
    <div key={engagement.id} className="nexgate-list-engagement-card">
      <div className="nexgate-list-card-header">
        <div className="nexgate-list-card-title">
          <div className="nexgate-list-type-icon">
            {getTypeIcon(engagement.engagement_type)}
          </div>
          <div className="nexgate-list-title-content">
            <h3 className="nexgate-list-engagement-title">{engagement.title}</h3>
            <div className="nexgate-list-engagement-meta">
              <span 
                className="nexgate-list-nexgate-id" 
                onClick={() => {
                  navigator.clipboard.writeText(engagement.nexgate_id);
                  // You could add a toast notification here
                }}
                title="Click to copy ID"
              >
                {engagement.nexgate_id}
              </span>
              <span className="nexgate-list-requester">
                <FeatherUser size={12} />
                {engagement.requester}
              </span>
            </div>
          </div>
        </div>
        <div className="nexgate-list-card-actions">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewEngagement(engagement)}
            title="View Details"
          >
            <FeatherEye size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEditEngagement(engagement)}
            title="Edit"
          >
            <FeatherEdit2 size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(engagement.id)}
            title="Delete"
            className="nexgate-list-delete-button"
          >
            <FeatherTrash2 size={16} />
          </Button>
        </div>
      </div>

      <div className="nexgate-list-card-content">
        <div className="nexgate-list-engagement-details">
          <div className="nexgate-list-detail-row">
            <div className="nexgate-list-detail-item">
              <FeatherTag size={14} />
              <span>Type:</span>
              <Badge variant="outline">
                {engagementTypes.find(t => t.value === engagement.engagement_type)?.label}
              </Badge>
            </div>
            <div className="nexgate-list-detail-item">
              <FeatherCalendar size={14} />
              <span>Start:</span>
              <span>{formatDate(engagement.start_date)}</span>
            </div>
            <div className="nexgate-list-detail-item">
              <FeatherClock size={14} />
              <span>Duration:</span>
              <span>{engagement.duration}</span>
            </div>
          </div>

          <div className="nexgate-list-detail-row">
            <div className="nexgate-list-detail-item">
              <span>Priority:</span>
              <Badge variant={getPriorityColor(engagement.priority)}>
                {priorities.find(p => p.value === engagement.priority)?.label}
              </Badge>
            </div>
            <div className="nexgate-list-detail-item">
              <span>Status:</span>
              <Badge variant={getStatusColor(engagement.status)}>
                {statuses.find(s => s.value === engagement.status)?.label}
              </Badge>
            </div>
            <div className="nexgate-list-detail-item">
              <span>Experience:</span>
              <span className="nexgate-list-experience-level">{engagement.experience_level}</span>
            </div>
          </div>

          <div className="nexgate-list-roles-section">
            <span>Required Roles:</span>
            <div className="nexgate-list-roles-list">
              {engagement.required_roles?.map(role => (
                <Badge key={role} variant="secondary" className="nexgate-list-role-badge">
                  {role}
                </Badge>
              ))}
            </div>
          </div>

          {engagement.tech_stack && engagement.tech_stack.length > 0 && (
            <div className="nexgate-list-tech-stack-section">
              <span>Tech Stack:</span>
              <div className="nexgate-list-tech-stack-list">
                {engagement.tech_stack.map(tech => (
                  <Badge key={tech} variant="outline" className="nexgate-list-tech-badge">
                    {tech}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {engagement.notes && (
            <div className="nexgate-list-notes-section">
              <span>Notes:</span>
              <p className="nexgate-list-notes-text">{engagement.notes}</p>
            </div>
          )}

          {engagement.attachments?.length > 0 && (
            <div className="nexgate-list-attachments-section">
              <span>Attachments:</span>
              <div className="nexgate-list-attachments-list">
                {engagement.attachments.map(attachment => (
                  <div key={attachment.id} className="nexgate-list-attachment-item">
                    <FeatherFileText size={12} />
                    <span>{attachment.file_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="nexgate-list-card-footer">
        <div className="nexgate-list-footer-info">
          <span>Created by {engagement.created_by_user?.display_name || 'Unknown'}</span>
          <span>on {formatDate(engagement.created_at)}</span>
        </div>
        {engagement.matches?.length > 0 && (
          <div className="nexgate-list-matches-info">
            <span>{engagement.matches.length} resource(s) matched</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="nexgate-list">
      <div className="nexgate-list-header">
        <div className="nexgate-list-header-content">
          <h1 className="nexgate-list-page-title">NexGate Engagements</h1>
          <p className="nexgate-list-page-subtitle">Manage resource augmentation and project staffing requests</p>
        </div>
        <div className="nexgate-list-header-actions">
                      <Button
              variant="secondary"
              onClick={() => setShowFilters(!showFilters)}
              className="nexgate-list-filter-toggle"
            >
              <FeatherFilter size={16} />
              Filters
            </Button>
          <Button
            variant="secondary"
            onClick={fetchEngagements}
            disabled={loading}
          >
            <FeatherRefreshCw size={16} />
            Refresh
          </Button>
          {onCreateNew ? (
            <Button
              onClick={() => {
                console.log('Create button clicked');
                onCreateNew();
              }}
              className="nexgate-list-create-button"
            >
              <FeatherPlus size={16} />
              New Engagement
            </Button>
          ) : (
            <div style={{ display: 'none' }}>Create button not rendered - onCreateNew is null</div>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="nexgate-list-search-filters">
        <form onSubmit={handleSearch} className="nexgate-list-search-form">
          <TextField>
            <TextField.Input
              placeholder="Search by title, requester, notes, or NexGate ID..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </TextField>
          <Button type="submit" disabled={loading}>
            <FeatherSearch size={16} />
            Search
          </Button>
        </form>

        {showFilters && (
          <div className="nexgate-list-filters-panel">
            <div className="nexgate-list-filters-grid">
              <div className="nexgate-list-filter-group">
                <label>Engagement Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <option value="">All Types</option>
                  {engagementTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="nexgate-list-filter-group">
                <label>Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="nexgate-list-filter-group">
                <label>Client/Requester</label>
                <input
                  type="text"
                  value={filters.client}
                  onChange={(e) => handleFilterChange('client', e.target.value)}
                  placeholder="Filter by client..."
                />
              </div>

              <div className="nexgate-list-filter-group">
                <label>Required Role</label>
                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                >
                  <option value="">All Roles</option>
                  {availableRoles.map(role => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>

              <div className="nexgate-list-filter-group">
                <label>Start Date From</label>
                <input
                  type="date"
                  value={filters.start_date_from}
                  onChange={(e) => handleFilterChange('start_date_from', e.target.value)}
                />
              </div>

              <div className="nexgate-list-filter-group">
                <label>Start Date To</label>
                <input
                  type="date"
                  value={filters.start_date_to}
                  onChange={(e) => handleFilterChange('start_date_to', e.target.value)}
                />
              </div>
            </div>

            <div className="nexgate-list-filter-actions">
              <Button variant="secondary" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="nexgate-list-error-message">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="nexgate-list-loading-state">
          <div className="nexgate-list-spinner"></div>
          <span>Loading engagements...</span>
        </div>
      )}

      {/* Engagements List */}
      {!loading && engagements.length === 0 && (
        <div className="nexgate-list-empty-state">
          <FeatherFileText size={48} />
          <h3>No engagements found</h3>
          <p>Create your first engagement to get started</p>
          <Button onClick={onCreateNew}>
            <FeatherPlus size={16} />
            Create Engagement
          </Button>
        </div>
      )}

      {!loading && engagements.length > 0 && (
        <div className="nexgate-list-engagements-grid">
          {engagements.map(renderEngagementCard)}
        </div>
      )}

      {/* Pagination */}
      {!loading && engagements.length > 0 && (
        <div className="nexgate-list-pagination">
          <div className="nexgate-list-pagination-info">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} engagements
          </div>
          <div className="nexgate-list-pagination-controls">
            <Button
              variant="secondary"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              <FeatherChevronLeft size={16} />
              Previous
            </Button>
            <span className="nexgate-list-page-info">
              Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
            </span>
            <Button
              variant="secondary"
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
              <FeatherChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NexGateList; 